/**
 * Text-to-speech with fallback to Google Translate TTS
 *
 * Primary: Web Speech API (browser native)
 * Fallback: Google Translate TTS audio (when speechSynthesis fails silently)
 */

const FALLBACK_TIMEOUT_MS = 500

/** Detect if text contains Chinese characters */
function hasChinese(text: string): boolean {
  return /[\u4e00-\u9fff\u3400-\u4dbf]/.test(text)
}

/**
 * Pick the correct English text for TTS.
 * If front is Chinese (AI generated front/back swapped), use back instead.
 */
export function getEnglishText(front: string, back: string): string {
  if (hasChinese(front) && !hasChinese(back)) return back
  return front
}

function getGoogleTtsUrl(text: string, accent: 'us' | 'uk'): string {
  const tld = accent === 'uk' ? 'co.uk' : 'com'
  return `https://translate.google.${tld}/translate_tts?ie=UTF-8&q=${encodeURIComponent(text)}&tl=en&client=tw-ob`
}

function playAudioFallback(text: string, accent: 'us' | 'uk', onEnd?: () => void): void {
  const audio = new Audio(getGoogleTtsUrl(text, accent))
  audio.onended = () => onEnd?.()
  audio.onerror = () => onEnd?.()
  audio.play().catch(() => onEnd?.())
}

interface SpeakOptions {
  onstart?: () => void
  onend?: () => void
}

export function speak(text: string, accent: 'us' | 'uk', options?: SpeakOptions): void {
  const synth = window.speechSynthesis
  synth.cancel()

  let started = false

  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = accent === 'us' ? 'en-US' : 'en-GB'
  utterance.rate = 0.9

  // Try to pick a voice matching the accent
  const voices = synth.getVoices()
  const langPrefix = accent === 'us' ? 'en-US' : 'en-GB'
  const matchedVoice = voices.find(v => v.lang === langPrefix)
    || voices.find(v => v.lang.startsWith('en'))
  if (matchedVoice) {
    utterance.voice = matchedVoice
  }

  // If speech doesn't start within timeout, fallback to Google TTS
  const fallbackTimer = setTimeout(() => {
    if (!started) {
      synth.cancel()
      playAudioFallback(text, accent, options?.onend)
    }
  }, FALLBACK_TIMEOUT_MS)

  utterance.onstart = () => {
    started = true
    clearTimeout(fallbackTimer)
    options?.onstart?.()
  }

  utterance.onend = () => options?.onend?.()

  utterance.onerror = () => {
    clearTimeout(fallbackTimer)
    if (!started) {
      playAudioFallback(text, accent, options?.onend)
    } else {
      options?.onend?.()
    }
  }

  synth.speak(utterance)
}
